import { useState, useEffect } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

export default function RewardClaimManualPage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar isScrolled={isScrolled} />

      {/* Breadcrumb */}
      <div className="pt-28 pb-6 px-6 bg-gradient-to-br from-pink-50 via-white to-rose-50">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-3 text-base">
            <a href="/" className="text-gray-500 hover:text-pink-600 transition-colors cursor-pointer">
              TOP
            </a>
            <i className="ri-arrow-right-s-line text-gray-400"></i>
            <span className="text-gray-900 font-medium">リワード（CLAIM）マニュアル</span>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="pb-16 px-6 bg-gradient-to-br from-pink-50 via-white to-rose-50">
        <div className="max-w-5xl mx-auto text-center">
          <div className="w-24 h-24 rounded-3xl bg-gradient-to-br from-pink-400 to-pink-600 flex items-center justify-center mx-auto mb-8">
            <i className="ri-gift-line text-5xl text-white"></i>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            リワード（CLAIM）<br />マニュアル
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 leading-relaxed max-w-3xl mx-auto mb-12">
            報酬の確認から受け取りまで、<br className="md:hidden" />
            やさしく丁寧に解説します
          </p>

          {/* Quick Navigation */}
          <div className="bg-white rounded-3xl p-8 shadow-lg border border-pink-100 max-w-3xl mx-auto">
            <h2 className="text-xl font-bold text-gray-900 mb-6">このページの内容</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button
                onClick={() => scrollToSection('what-is-reward')}
                className="px-6 py-4 bg-gradient-to-br from-pink-50 to-rose-50 text-gray-900 rounded-xl font-medium hover:shadow-md transition-all text-left flex items-center gap-3 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-question-line text-2xl text-pink-600"></i>
                <span>リワードとは？</span>
              </button>
              <button
                onClick={() => scrollToSection('how-to-check')}
                className="px-6 py-4 bg-gradient-to-br from-pink-50 to-rose-50 text-gray-900 rounded-xl font-medium hover:shadow-md transition-all text-left flex items-center gap-3 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-search-line text-2xl text-pink-600"></i>
                <span>確認方法</span>
              </button>
              <button
                onClick={() => scrollToSection('claim-steps')}
                className="px-6 py-4 bg-gradient-to-br from-pink-50 to-rose-50 text-gray-900 rounded-xl font-medium hover:shadow-md transition-all text-left flex items-center gap-3 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-hand-coin-line text-2xl text-pink-600"></i>
                <span>CLAIM手順</span>
              </button>
              <button
                onClick={() => scrollToSection('gas-fee')}
                className="px-6 py-4 bg-gradient-to-br from-pink-50 to-rose-50 text-gray-900 rounded-xl font-medium hover:shadow-md transition-all text-left flex items-center gap-3 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-gas-station-line text-2xl text-pink-600"></i>
                <span>ガス代とは？</span>
              </button>
              <button
                onClick={() => scrollToSection('lock-comparison')}
                className="px-6 py-4 bg-gradient-to-br from-pink-50 to-rose-50 text-gray-900 rounded-xl font-medium hover:shadow-md transition-all text-left flex items-center gap-3 cursor-pointer whitespace-nowrap md:col-span-2"
              >
                <i className="ri-scales-line text-2xl text-pink-600"></i>
                <span>ロックする場合／しない場合の違い</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* What is Reward Section */}
      <section id="what-is-reward" className="py-20 px-6 scroll-mt-24">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-pink-400 to-pink-600 flex items-center justify-center flex-shrink-0">
              <i className="ri-question-line text-3xl text-white"></i>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900">
              リワードとは？
            </h2>
          </div>

          <div className="bg-gradient-to-br from-pink-50 to-rose-50 rounded-3xl p-10 md:p-14 border border-pink-100 mb-8">
            <p className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 leading-relaxed">
              リワード = あなたへの「ありがとう」
            </p>
            <p className="text-xl text-gray-700 leading-relaxed mb-6">
              MAWARIノードを運営してくれているあなたへの感謝の気持ちです。
            </p>
            <p className="text-xl text-gray-700 leading-relaxed mb-6">
              毎日コツコツと貯まっていき、好きなタイミングで受け取ることができます。
            </p>
          </div>

          {/* Image Placeholder */}
          <div className="bg-gradient-to-br from-gray-100 to-gray-50 rounded-3xl overflow-hidden border border-gray-200">
            <div className="w-full h-96 flex items-center justify-center">
              <img
                src="https://public.readdy.ai/ai/img_res/c9fc1d5d9e8adfd0ad49fb2a2edf3b4b.jpg"
                alt="リワードのイメージ"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* How to Check Section */}
      <section id="how-to-check" className="py-20 px-6 bg-gradient-to-br from-gray-50 to-white scroll-mt-24">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-pink-400 to-pink-600 flex items-center justify-center flex-shrink-0">
              <i className="ri-search-line text-3xl text-white"></i>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900">
              確認方法
            </h2>
          </div>

          <p className="text-xl text-gray-600 mb-12 leading-relaxed">
            今どれくらい貯まっているか、<br className="md:hidden" />
            いつでも簡単に確認できます。
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <div className="bg-white rounded-3xl p-8 shadow-lg border border-gray-100">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-pink-400 to-pink-600 flex items-center justify-center mb-6">
                <span className="text-2xl font-bold text-white">1</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">ダッシュボードを開く</h3>
              <p className="text-lg text-gray-600 leading-relaxed mb-4">
                MAWARIプラットフォームにログインして、<br />
                ダッシュボード画面を開きます。
              </p>
              <div className="bg-pink-50 rounded-xl p-4 border border-pink-100">
                <p className="text-base text-gray-700">
                  <strong className="text-pink-600">場所：</strong> 画面上部の「ダッシュボード」ボタン
                </p>
              </div>
            </div>

            <div className="bg-white rounded-3xl p-8 shadow-lg border border-gray-100">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-pink-400 to-pink-600 flex items-center justify-center mb-6">
                <span className="text-2xl font-bold text-white">2</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">リワード欄を見る</h3>
              <p className="text-lg text-gray-600 leading-relaxed mb-4">
                「獲得可能なリワード」という欄に、<br />
                現在の報酬額が表示されています。
              </p>
              <div className="bg-pink-50 rounded-xl p-4 border border-pink-100">
                <p className="text-base text-gray-700">
                  <strong className="text-pink-600">表示：</strong> リアルタイムで更新されます
                </p>
              </div>
            </div>
          </div>

          {/* Image Placeholder */}
          <div className="bg-gradient-to-br from-gray-100 to-gray-50 rounded-3xl overflow-hidden border border-gray-200">
            <div className="w-full h-96 flex items-center justify-center">
              <img
                src="https://readdy.ai/api/search-image?query=A%20clean%20modern%20dashboard%20interface%20screenshot%20showing%20reward%20balance%20and%20statistics%20with%20clear%20numbers%20and%20charts%20soft%20pink%20accent%20colors%20on%20white%20background%20professional%20web%20application%20design%20easy%20to%20read%20large%20fonts%20minimalist%20UI%20elements%20displaying%20cryptocurrency%20rewards%20and%20earnings%20user%20friendly%20layout%20with%20prominent%20reward%20display%20section&width=1200&height=400&seq=dashboard-check-002&orientation=landscape"
                alt="ダッシュボード確認画面"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CLAIM Steps Section */}
      <section id="claim-steps" className="py-20 px-6 scroll-mt-24">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-pink-400 to-pink-600 flex items-center justify-center flex-shrink-0">
              <i className="ri-hand-coin-line text-3xl text-white"></i>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900">
              CLAIM手順
            </h2>
          </div>

          <p className="text-xl text-gray-600 mb-12 leading-relaxed">
            リワードを受け取る手順を、<br className="md:hidden" />
            ステップごとに説明します。
          </p>

          <div className="space-y-8">
            {/* Step 1 */}
            <div className="bg-white rounded-3xl p-8 md:p-10 shadow-lg border border-gray-100">
              <div className="flex items-start gap-6">
                <div className="flex-shrink-0 w-16 h-16 rounded-2xl bg-gradient-to-br from-pink-400 to-pink-600 flex items-center justify-center">
                  <span className="text-2xl font-bold text-white">1</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                    ダッシュボードを開く
                  </h3>
                  <p className="text-xl text-gray-600 leading-relaxed mb-4">
                    MAWARIプラットフォームにログインして、<br />
                    ダッシュボード画面を開きます。
                  </p>
                  <p className="text-xl text-gray-600 leading-relaxed">
                    獲得可能なリワードの金額を確認してください。
                  </p>
                </div>
              </div>
            </div>

            {/* Step 2 */}
            <div className="bg-white rounded-3xl p-8 md:p-10 shadow-lg border border-gray-100">
              <div className="flex items-start gap-6">
                <div className="flex-shrink-0 w-16 h-16 rounded-2xl bg-gradient-to-br from-pink-400 to-pink-600 flex items-center justify-center">
                  <span className="text-2xl font-bold text-white">2</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                    「CLAIM」ボタンをクリック
                  </h3>
                  <p className="text-xl text-gray-600 leading-relaxed mb-4">
                    リワード欄の近くにある<br />
                    「CLAIM」または「報酬を受け取る」ボタンを<br />
                    クリックします。
                  </p>
                  <div className="bg-pink-50 rounded-xl p-5 border border-pink-100">
                    <p className="text-lg text-gray-700">
                      <strong className="text-pink-600">ポイント：</strong> ボタンが押せない場合は、まだ受け取れる報酬がない可能性があります。
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 3 */}
            <div className="bg-white rounded-3xl p-8 md:p-10 shadow-lg border border-gray-100">
              <div className="flex items-start gap-6">
                <div className="flex-shrink-0 w-16 h-16 rounded-2xl bg-gradient-to-br from-pink-400 to-pink-600 flex items-center justify-center">
                  <span className="text-2xl font-bold text-white">3</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                    内容を確認する
                  </h3>
                  <p className="text-xl text-gray-600 leading-relaxed mb-4">
                    確認画面が表示されます。<br />
                    以下の内容をチェックしてください：
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-2xl text-pink-600 flex-shrink-0"></i>
                      <span className="text-xl text-gray-600">受け取る報酬の金額</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-2xl text-pink-600 flex-shrink-0"></i>
                      <span className="text-xl text-gray-600">ガス代（手数料）の金額</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-2xl text-pink-600 flex-shrink-0"></i>
                      <span className="text-xl text-gray-600">受け取り先のウォレットアドレス</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Step 4 */}
            <div className="bg-white rounded-3xl p-8 md:p-10 shadow-lg border border-gray-100">
              <div className="flex items-start gap-6">
                <div className="flex-shrink-0 w-16 h-16 rounded-2xl bg-gradient-to-br from-pink-400 to-pink-600 flex items-center justify-center">
                  <span className="text-2xl font-bold text-white">4</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                    ウォレットで承認する
                  </h3>
                  <p className="text-xl text-gray-600 leading-relaxed mb-4">
                    MetaMaskなどのウォレットが開きます。<br />
                    内容を確認して「承認」ボタンを押してください。
                  </p>
                  <div className="bg-yellow-50 rounded-xl p-5 border border-yellow-200">
                    <p className="text-lg text-gray-700">
                      <strong className="text-yellow-600">注意：</strong> この時点でガス代（手数料）が発生します。
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 5 */}
            <div className="bg-white rounded-3xl p-8 md:p-10 shadow-lg border border-gray-100">
              <div className="flex items-start gap-6">
                <div className="flex-shrink-0 w-16 h-16 rounded-2xl bg-gradient-to-br from-pink-400 to-pink-600 flex items-center justify-center">
                  <span className="text-2xl font-bold text-white">5</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                    完了を待つ
                  </h3>
                  <p className="text-xl text-gray-600 leading-relaxed mb-4">
                    トランザクションが処理されるまで<br />
                    少し待ちます（通常1〜3分程度）。
                  </p>
                  <p className="text-xl text-gray-600 leading-relaxed mb-4">
                    完了すると、リワードがあなたのウォレットに<br />
                    送られます。
                  </p>
                  <div className="bg-green-50 rounded-xl p-5 border border-green-200">
                    <p className="text-lg text-gray-700">
                      <strong className="text-green-600">完了！</strong> ウォレットの残高を確認してみてください。
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Image Placeholder */}
          <div className="mt-12 bg-gradient-to-br from-gray-100 to-gray-50 rounded-3xl overflow-hidden border border-gray-200">
            <div className="w-full h-96 flex items-center justify-center">
              <img
                src="https://readdy.ai/api/search-image?query=A%20step%20by%20step%20visual%20guide%20showing%20the%20process%20of%20claiming%20cryptocurrency%20rewards%20with%20numbered%20steps%20and%20arrows%20clean%20modern%20interface%20screenshots%20soft%20pink%20and%20white%20color%20scheme%20showing%20wallet%20connection%20claim%20button%20and%20confirmation%20screens%20easy%20to%20follow%20tutorial%20style%20illustration%20professional%20yet%20friendly%20design%20minimalist%20UI%20elements&width=1200&height=400&seq=claim-steps-003&orientation=landscape"
                alt="CLAIM手順の流れ"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Gas Fee Section */}
      <section id="gas-fee" className="py-20 px-6 bg-gradient-to-br from-gray-50 to-white scroll-mt-24">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-pink-400 to-pink-600 flex items-center justify-center flex-shrink-0">
              <i className="ri-gas-station-line text-3xl text-white"></i>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900">
              ガス代とは？
            </h2>
          </div>

          <div className="bg-gradient-to-br from-pink-50 to-rose-50 rounded-3xl p-10 md:p-14 border border-pink-100 mb-8">
            <p className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 leading-relaxed">
              ガス代 = ブロックチェーンの利用料
            </p>
            <p className="text-xl text-gray-700 leading-relaxed mb-6">
              リワードを受け取る時には、<br />
              ブロックチェーンを使って取引を記録します。
            </p>
            <p className="text-xl text-gray-700 leading-relaxed mb-6">
              その時にかかる手数料が「ガス代」です。
            </p>
            <div className="bg-white rounded-2xl p-8 border border-pink-200">
              <div className="flex items-start gap-4">
                <i className="ri-lightbulb-line text-3xl text-pink-600 flex-shrink-0"></i>
                <div>
                  <p className="text-lg font-bold text-gray-900 mb-3">わかりやすく例えると...</p>
                  <p className="text-lg text-gray-700 leading-relaxed">
                    銀行でお金を引き出す時の手数料のようなもの。<br />
                    ただし、金額は時間帯によって変わります。<br />
                    混雑している時は高く、空いている時は安くなります。
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white rounded-3xl p-8 shadow-lg border border-gray-100">
              <div className="w-14 h-14 rounded-xl bg-green-100 flex items-center justify-center mb-6">
                <i className="ri-time-line text-2xl text-green-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">節約のコツ</h3>
              <p className="text-lg text-gray-600 leading-relaxed mb-4">
                ガス代を安くするには：
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-circle-fill text-xl text-pink-600 flex-shrink-0"></i>
                  <span className="text-lg text-gray-600">深夜や早朝など、空いている時間帯を選ぶ</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-circle-fill text-xl text-pink-600 flex-shrink-0"></i>
                  <span className="text-lg text-gray-600">ある程度リワードが貯まってからまとめて受け取る</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-3xl p-8 shadow-lg border border-gray-100">
              <div className="w-14 h-14 rounded-xl bg-blue-100 flex items-center justify-center mb-6">
                <i className="ri-calculator-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">目安の金額</h3>
              <p className="text-lg text-gray-600 leading-relaxed mb-4">
                ガス代の目安：
              </p>
              <div className="space-y-4">
                <div className="bg-gradient-to-br from-pink-50 to-rose-50 rounded-xl p-5 border border-pink-100">
                  <div className="text-base text-gray-600 mb-1">空いている時</div>
                  <div className="text-2xl font-bold text-pink-600">$2〜$5程度</div>
                </div>
                <div className="bg-gradient-to-br from-pink-50 to-rose-50 rounded-xl p-5 border border-pink-100">
                  <div className="text-base text-gray-600 mb-1">混雑している時</div>
                  <div className="text-2xl font-bold text-pink-600">$10〜$30程度</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Lock Comparison Section */}
      <section id="lock-comparison" className="py-20 px-6 scroll-mt-24">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-pink-400 to-pink-600 flex items-center justify-center flex-shrink-0">
              <i className="ri-scales-line text-3xl text-white"></i>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900">
              ロックする場合／<br className="md:hidden" />しない場合の違い
            </h2>
          </div>

          <p className="text-xl text-gray-600 mb-12 leading-relaxed">
            リワードを受け取った後、<br className="md:hidden" />
            どうするか選べます。
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            {/* Lock Option */}
            <div className="bg-gradient-to-br from-pink-50 to-rose-50 rounded-3xl p-8 md:p-10 border border-pink-200">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-pink-400 to-pink-600 flex items-center justify-center mb-6">
                <i className="ri-lock-line text-3xl text-white"></i>
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-6">ロックする場合</h3>
              
              <div className="space-y-6">
                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-3">メリット</h4>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <i className="ri-check-line text-2xl text-green-600 flex-shrink-0"></i>
                      <span className="text-lg text-gray-700">さらに高い報酬率で増やせる</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-check-line text-2xl text-green-600 flex-shrink-0"></i>
                      <span className="text-lg text-gray-700">複利効果で雪だるま式に増える</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-check-line text-2xl text-green-600 flex-shrink-0"></i>
                      <span className="text-lg text-gray-700">長期的な資産形成に最適</span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-3">デメリット</h4>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <i className="ri-close-line text-2xl text-red-600 flex-shrink-0"></i>
                      <span className="text-lg text-gray-700">ロック期間中は引き出せない</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-close-line text-2xl text-red-600 flex-shrink-0"></i>
                      <span className="text-lg text-gray-700">すぐに使えるお金にはならない</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-white rounded-2xl p-6 border border-pink-200">
                  <p className="text-lg font-bold text-pink-600 mb-2">こんな人におすすめ</p>
                  <p className="text-base text-gray-700 leading-relaxed">
                    長期的に資産を増やしたい方<br />
                    すぐに使う予定がない方
                  </p>
                </div>
              </div>
            </div>

            {/* No Lock Option */}
            <div className="bg-white rounded-3xl p-8 md:p-10 border border-gray-200 shadow-lg">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-gray-400 to-gray-600 flex items-center justify-center mb-6">
                <i className="ri-lock-unlock-line text-3xl text-white"></i>
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-6">ロックしない場合</h3>
              
              <div className="space-y-6">
                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-3">メリット</h4>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <i className="ri-check-line text-2xl text-green-600 flex-shrink-0"></i>
                      <span className="text-lg text-gray-700">すぐに自由に使える</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-check-line text-2xl text-green-600 flex-shrink-0"></i>
                      <span className="text-lg text-gray-700">他の投資に回せる</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-check-line text-2xl text-green-600 flex-shrink-0"></i>
                      <span className="text-lg text-gray-700">柔軟に対応できる</span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-3">デメリット</h4>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <i className="ri-close-line text-2xl text-red-600 flex-shrink-0"></i>
                      <span className="text-lg text-gray-700">追加の報酬は得られない</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-close-line text-2xl text-red-600 flex-shrink-0"></i>
                      <span className="text-lg text-gray-700">複利効果が得られない</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-6 border border-gray-200">
                  <p className="text-lg font-bold text-gray-700 mb-2">こんな人におすすめ</p>
                  <p className="text-base text-gray-700 leading-relaxed">
                    すぐに使いたい方<br />
                    柔軟に運用したい方
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Comparison Table */}
          <div className="bg-white rounded-3xl p-8 md:p-10 shadow-lg border border-gray-100">
            <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">比較表</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-gray-200">
                    <th className="text-left py-4 px-4 text-lg font-bold text-gray-900">項目</th>
                    <th className="text-center py-4 px-4 text-lg font-bold text-pink-600">ロックする</th>
                    <th className="text-center py-4 px-4 text-lg font-bold text-gray-600">ロックしない</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-100">
                    <td className="py-4 px-4 text-lg text-gray-700">追加報酬</td>
                    <td className="text-center py-4 px-4">
                      <span className="inline-block px-4 py-2 bg-green-100 text-green-700 rounded-full font-medium text-base">あり</span>
                    </td>
                    <td className="text-center py-4 px-4">
                      <span className="inline-block px-4 py-2 bg-gray-100 text-gray-700 rounded-full font-medium text-base">なし</span>
                    </td>
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="py-4 px-4 text-lg text-gray-700">引き出し</td>
                    <td className="text-center py-4 px-4">
                      <span className="inline-block px-4 py-2 bg-red-100 text-red-700 rounded-full font-medium text-base">期間後</span>
                    </td>
                    <td className="text-center py-4 px-4">
                      <span className="inline-block px-4 py-2 bg-green-100 text-green-700 rounded-full font-medium text-base">いつでも</span>
                    </td>
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="py-4 px-4 text-lg text-gray-700">複利効果</td>
                    <td className="text-center py-4 px-4">
                      <span className="inline-block px-4 py-2 bg-green-100 text-green-700 rounded-full font-medium text-base">あり</span>
                    </td>
                    <td className="text-center py-4 px-4">
                      <span className="inline-block px-4 py-2 bg-gray-100 text-gray-700 rounded-full font-medium text-base">なし</span>
                    </td>
                  </tr>
                  <tr>
                    <td className="py-4 px-4 text-lg text-gray-700">おすすめ度</td>
                    <td className="text-center py-4 px-4">
                      <div className="flex justify-center gap-1">
                        <i className="ri-star-fill text-2xl text-yellow-400"></i>
                        <i className="ri-star-fill text-2xl text-yellow-400"></i>
                        <i className="ri-star-fill text-2xl text-yellow-400"></i>
                        <i className="ri-star-fill text-2xl text-yellow-400"></i>
                        <i className="ri-star-fill text-2xl text-yellow-400"></i>
                      </div>
                    </td>
                    <td className="text-center py-4 px-4">
                      <div className="flex justify-center gap-1">
                        <i className="ri-star-fill text-2xl text-yellow-400"></i>
                        <i className="ri-star-fill text-2xl text-yellow-400"></i>
                        <i className="ri-star-fill text-2xl text-yellow-400"></i>
                        <i className="ri-star-line text-2xl text-gray-300"></i>
                        <i className="ri-star-line text-2xl text-gray-300"></i>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-pink-500 via-purple-500 to-pink-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            さあ、リワードを<br className="md:hidden" />受け取りましょう
          </h2>
          <p className="text-xl text-white/90 mb-10 leading-relaxed">
            あなたの貢献への感謝の気持ちを、<br className="md:hidden" />
            今すぐ受け取ってください
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="px-10 py-5 bg-white text-pink-600 text-xl rounded-full font-bold hover:shadow-2xl transition-all hover:scale-105 whitespace-nowrap cursor-pointer">
              ダッシュボードへ
            </button>
            <button
              onClick={() => scrollToSection('what-is-reward')}
              className="px-10 py-5 bg-white/10 backdrop-blur-sm text-white text-xl rounded-full font-bold hover:bg-white/20 transition-all border-2 border-white/30 whitespace-nowrap cursor-pointer"
            >
              もう一度読む
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}